import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouter } from "@tanstack/react-router";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import { PremiumGateProvider } from "@/hooks/usePremiumGate";
import { Toaster } from "sonner";
import { useInAppNotifications } from "@/hooks/useInAppNotifications";
import { useEntitlementSync } from "@/hooks/useEntitlementSync";
import { useSnapAppEvents } from "@/hooks/useSnapAppEvents";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { PushOptInBanner } from "@/components/NotificationSettings";
import { PastDueBanner } from "@/components/PastDueBanner";
import { AppNavigationControls } from "@/components/AppNavigationControls";

import { useEffect } from "react";
import { captureReferralSlug, attachReferralSlugIfPresent, initPosthog, identifyPosthog, initSentry, identifySentry, installAnalyticsFailureMonitor, maybeFireSentrySmokeTest } from "@/lib/analytics";
import { trackAffiliateVisit, bindAffiliateAttribution } from "@/lib/affiliate";
import { installNativeBridge } from "@/lib/native-bridge";
import { initRokuPixelWhenConsented, trackRokuPageView } from "@/lib/roku";
import "@/i18n";

import appCss from "../styles.css?url";
import { BOOT_FALLBACK_SCRIPT } from "@/lib/boot-fallback";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          Page not found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      // Sitewide defaults only. Title, description, og:* and twitter:* are
      // defined per leaf route so the document head is never duplicated.
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "author", content: "Inquira" },
      { name: "google-site-verification", content: "0WPS6rgvSEbsXRc8mXBjJaigulRQoOQlyGLlaQ3CsTE" },
      { property: "og:site_name", content: "Inquira" },
      { name: "contact", content: "info@inquira.online" },
      { name: "reply-to", content: "info@inquira.online" },
    ],
    links: [
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "icon", type: "image/png", href: "/favicon.png", sizes: "64x64" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous" as const,
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Outfit:wght@400;600;800&family=JetBrains+Mono:wght@400;700&display=swap",
      },
    ],
    scripts: [
      {
        children:
          "try{var t=localStorage.getItem('mimi-theme');var d=t?t==='dark':matchMedia('(prefers-color-scheme: dark)').matches;document.documentElement.classList.toggle('dark',d)}catch(e){}",
      },
      {
        // Guarantees a branded, recoverable screen instead of a blank white
        // WebView if anything fails before React mounts (App Review 2.1(a)).
        children: BOOT_FALLBACK_SCRIPT,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Inquira",
              url: "https://www.inquira.online",
              logo: "https://www.inquira.online/app-icon-512.png",
              email: "info@inquira.online",
            },
            {
              "@type": "WebSite",
              name: "Inquira",
              url: "https://www.inquira.online",
              description:
                "AI study companion that turns any problem into clear, step-by-step solutions in 70+ languages.",
            },
          ],
        }),
      },
      ...(import.meta.env.VITE_GOOGLE_ADS_ID
        ? [
            { src: `https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GOOGLE_ADS_ID}`, async: true },
            { children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${import.meta.env.VITE_GOOGLE_ADS_ID}');` },
          ]
        : []),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // Any successful React mount (including the router's error screen) retires
  // the pre-mount watchdog, so the fallback only ever covers a real dead boot.
  useEffect(() => {
    (window as unknown as { __inquiraBoot?: { clear?: () => void } }).__inquiraBoot?.clear?.();
  }, []);
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function NotificationListener() {
  useInAppNotifications();
  return null;
}

/** Rechecks store entitlements on launch and when the app is foregrounded. */
function EntitlementSync() {
  useEntitlementSync();
  return null;
}

function InnerRoot() {
  const { user } = useAuth();
  const router = useRouter();
  useSnapAppEvents(user ?? null);
  useEffect(() => {
    // Roku Ads pixel: loads only after explicit ad consent, then one
    // PAGE_VIEW per client-side navigation.
    initRokuPixelWhenConsented();
    return router.subscribe("onResolved", ({ toLocation }) => {
      trackRokuPageView(toLocation.pathname);
    });
  }, [router]);
  useEffect(() => {
    captureReferralSlug();
    void trackAffiliateVisit();
    // Exposes window.InquiraNative from the Capacitor billing plugin (see native/).
    // React is alive: retire the pre-mount fallback screen / watchdog.
    (window as unknown as { __inquiraBoot?: { clear?: () => void } }).__inquiraBoot?.clear?.();
    installNativeBridge();
    installAnalyticsFailureMonitor();
    initPosthog();
    initSentry();
    maybeFireSentrySmokeTest();
  }, []);
  useEffect(() => {
    if (user?.id) {
      attachReferralSlugIfPresent(user.id);
      void bindAffiliateAttribution();
      identifyPosthog(user.id, { email: user.email });
      identifySentry(user.id, user.email);
    }
  }, [user?.id]);
  return (
    <LanguageProvider userId={user?.id}>
      <NotificationListener />
      <EntitlementSync />
      <Toaster position="top-center" richColors />
      <PastDueBanner />
      <PushOptInBanner />
      <AppNavigationControls />
      <Outlet />
      <FeedbackWidget />
    </LanguageProvider>
  );

}

function RootComponent() {
  return (
    <AuthProvider>
      <PremiumGateProvider>
        <InnerRoot />
      </PremiumGateProvider>
    </AuthProvider>
  );
}
